// @flow
import React, { useEffect, useRef, useState } from 'react';
import { isEmpty, get } from 'lodash';
import classnames from 'classnames';

import { messageFormat } from '@clubmed/utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Languages, type LanguagesProps } from 'Components/widgets/languages/Languages.jsx';

import { getNavigationLanguagesButtonTrackingData } from 'Components/widgets/navigation/Navigation.tracking';

import { SideNavigationItem } from './SideNavigationItem.jsx';

export type NavItem = {
  color: Colors,
  label: string,
  link?: {
    target?: string,
    trackingData?: ?TrackingData,
    url: ?string,
  },
  subItems: Array<NavItem>,
};

type Props = {
  backButtonLabel?: string,
  backToB2CWebsiteLabel?: string,
  backToB2CWebsiteLink?: string,
  closeMenu: () => void,
  id: string,
  items: Array<NavItem>,
  isActive?: boolean,
  isHidden?: boolean,
  isMicePage?: boolean,
  label?: string,
  language?: LanguagesProps,
  navBack?: () => void,
  navForward: (nextPanel: string) => void,
  parent?: string,
  parentLabel?: string,
  parentLink?: {
    url?: ?string,
    target?: string,
    trackingData?: ?TrackingData,
  },
  style?: *,
};

export function SideNavigation(props: Props) {
  const {
    backButtonLabel,
    backToB2CWebsiteLabel,
    backToB2CWebsiteLink,
    closeMenu,
    id = 'SideNavigation',
    items = [],
    isActive,
    isHidden,
    isMicePage,
    label,
    language,
    navBack,
    navForward,
    parent,
    parentLabel,
    parentLink,
    style,
  } = props;

  const [activeItem, setActiveItem] = useState(-1);

  const backButtonRef: { current: ?HTMLElement } = useRef(null);

  useEffect(
    () => {
      if (!isHidden && backButtonRef && backButtonRef.current) {
        backButtonRef.current.focus();
      }
      if (isHidden) {
        setActiveItem(-1);
      }
    },
    [isHidden],
  );

  const classNames = isHidden ? 'rtl:-translate-x-100' : 'translate-x-0';
  const hasParentLink = parentLink && parentLink.url;

  const navItems = items.map((item, idx) => {
    const itemProps = {
      color: item.color,
      isRoot: isEmpty(parent),
      label: item.label,
      link: {},
      nextPanel: '',
      onClick: navForward,
      setActiveItem: () => {
        setActiveItem(idx);
      },
      tabIndex: isActive ? 0 : -1,
    };

    const subItems = item.subItems || [];

    if (subItems.length) {
      const itemId = `${item.label.replace(/ /g, '-').toLowerCase()}`;
      itemProps.nextPanel = itemId;
    } else {
      itemProps.link = get(item, 'link');
    }

    return (
      <li key={itemProps.label}>
        <SideNavigationItem {...itemProps} hasFocus={isActive && activeItem === idx} />
      </li>
    );
  });

  return (
    <div
      aria-hidden={isHidden}
      className={classnames('bg-white fixed bottom-0 w-full rtl:left-0', classNames)}
      id={id}
      style={style}
    >
      <div className="bg-white h-full overflow-y-scroll p-4">
        {!!parent &&
          !!label && (
            <div className="flex justify-between">
              <button
                className="reset-button flex font-bold items-center p-3 text-base rtl:-ml-3"
                data-testid="SideNavigation-back"
                onClick={navBack ? () => navBack() : null}
                ref={backButtonRef}
                tabIndex={isActive ? 0 : -1}
                type="button"
              >
                <Icon
                  color={COLORS.BLUE}
                  hasRtl
                  name={ICONS.BOLDER_ARROW_FORTH}
                  rotation={180}
                  width="0.8125rem"
                />
                {!!backButtonLabel && (
                  <span className="a11y" data-testid="SideNavigation-back-label">
                    {messageFormat(backButtonLabel).format({ item: parentLabel })}
                  </span>
                )}
              </button>
              <TrackClick
                {...(hasParentLink ? parentLink : null)}
                as={hasParentLink ? DynamicLink : 'div'}
                className="block font-bold py-3 text-blue text-md uppercase"
                onClick={closeMenu}
                data-testid="SideNavigation-item-link"
                tabIndex={isActive ? 0 : -1}
                trackingData={parentLink && parentLink.trackingData}
              >
                {label}
              </TrackClick>
            </div>
          )}

        <ul className="reset-list">{!isEmpty(items) && navItems}</ul>

        {!parent &&
          isMicePage &&
          backToB2CWebsiteLabel && (
            <TrackClick
              as={DynamicLink}
              trackingData={{
                categoryGA: 'headerMenu',
                actionGA: 'header_backToB2C',
                labelGA: 'backToB2C',
              }}
              className="block text-md py-4 leading-tight font-bold underline reset-button cursor-pointer relative focus:text-blue hover:text-blue text-gray-darker"
              url={backToB2CWebsiteLink}
              onClick={closeMenu}
            >
              {backToB2CWebsiteLabel}
            </TrackClick>
          )}

        {!parent &&
          language &&
          Object.keys(get(language, 'locales', {})).length > 1 && (
            <div className="border-b-1 border-t-1 border-gray-light overflow-x-auto">
              <Languages
                {...language}
                className="min-w-full py-4"
                getTrackingData={getNavigationLanguagesButtonTrackingData}
                isActive={isActive}
              />
            </div>
          )}
      </div>
    </div>
  );
}
